<template>
  <section class="section scenario-section" id="features">
    <div class="split-layout">
      <div class="section-copy">
        <h2 class="section-title">{{ $t('home.scenario.title') }}</h2>
        <p>{{ $t('home.scenario.subtitle') }}</p>
      </div>

      <ol class="scenario-list">
        <li v-for="(scenario, index) in scenarios" :key="scenario.id">
          <button type="button" class="scenario-row" @click="openScenario(scenario.id)">
            <span class="scenario-index" aria-hidden="true">{{ String(index + 1).padStart(2, '0') }}</span>
            <span class="scenario-text">
              <strong>{{ scenario.title }}</strong>
              <span class="scenario-desc">{{ scenario.description }}</span>
            </span>
            <span class="scenario-action">
              {{ scenario.action }}
              <span class="material-icons" aria-hidden="true">arrow_forward</span>
            </span>
          </button>
        </li>
      </ol>
    </div>
  </section>
</template>

<script>
import { useViews } from '@/composables/views';
import { useModals } from '@/composables/modals';

const SCENARIO_IDS = ['ielts', 'vlog', 'interview', 'pronunciation'];

export default {
  name: 'HomeScenarioSection',
  props: {
    isLoggedIn: {
      type: Boolean,
      required: true
    }
  },
  emits: ['demo'],
  setup() {
    const { handleViewChange } = useViews();
    const { showLoginModal, showTextToSpeech } = useModals();
    return { handleViewChange, showLoginModal, showTextToSpeech };
  },
  computed: {
    scenarios() {
      const items = this.$tm('home.scenario.items');
      return SCENARIO_IDS.map((id) => ({ ...items[id], id }));
    }
  },
  methods: {
    openScenario(id) {
      if (id === 'ielts') {
        this.handleViewChange('Material', { categoryId: 'ielts' });
      } else if (id === 'vlog') {
        this.handleViewChange('Material', { categoryId: 'youtube-vlog' });
      } else if (id === 'interview') {
        if (this.isLoggedIn) {
          this.showTextToSpeech();
        } else {
          this.showLoginModal();
        }
      } else {
        this.$emit('demo');
      }
    }
  }
};
</script>

<style scoped>
.scenario-list {
  margin: 0;
  padding: 0;
  border-top: 1px solid var(--border-subtle);
  list-style: none;
}

.scenario-list li {
  border-bottom: 1px solid var(--border-subtle);
}

.scenario-row {
  display: grid;
  grid-template-columns: 2.5rem minmax(0, 1fr) auto;
  align-items: center;
  column-gap: var(--space-4);
  width: 100%;
  padding: var(--space-6) var(--space-4);
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 180ms ease;
}

.scenario-row:hover {
  background: var(--surface-panel);
}

.scenario-row:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

.scenario-index {
  align-self: start;
  padding-top: 0.4em;
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-sm);
  font-variant-numeric: tabular-nums;
}

.scenario-text {
  display: grid;
  gap: var(--space-1);
  min-width: 0;
}

.scenario-text strong {
  color: var(--ink-primary);
  font-size: 1.375rem;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: -0.01em;
}

.scenario-desc {
  color: var(--ink-secondary);
  font-size: 0.9375rem;
  line-height: 1.6;
}

.scenario-action {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  color: var(--signal-active-strong);
  font-size: 0.9375rem;
  font-weight: 600;
  white-space: nowrap;
}

.scenario-action .material-icons {
  font-size: 1rem;
  transition: transform 180ms ease;
}

.scenario-row:hover .scenario-action .material-icons {
  transform: translateX(2px);
}

@media (prefers-reduced-motion: reduce) {
  .scenario-row,
  .scenario-action .material-icons {
    transition: none;
  }

  .scenario-row:hover .scenario-action .material-icons {
    transform: none;
  }
}

/* 窄屏：入口文字换到说明下面 */
@media (max-width: 640px) {
  .scenario-row {
    grid-template-columns: 2rem minmax(0, 1fr);
    row-gap: var(--space-3);
    padding: var(--space-5) 0;
  }

  .scenario-row:hover {
    background: transparent;
  }

  .scenario-text strong {
    font-size: 1.1875rem;
  }

  .scenario-action {
    grid-column: 2;
  }
}
</style>
